import { CommonModule } from '@angular/common';
import {
  Component,
  ContentChild,
  EventEmitter,
  HostBinding,
  Input,
  Output,
} from '@angular/core';
import { FormsModule } from '@angular/forms';
import {
  DocTreeNode,
  DocTreeNodeSelectEvent,
  docTreeNodesMatch,
  getDocTreeNodeField,
} from './doc-tree.types';
import { DocTreeNodeTemplateDirective } from './doc-tree-node-template.directive';

@Component({
  selector: 'doc-tree',
  standalone: true,
  imports: [CommonModule, FormsModule, DocTreeNodeTemplateDirective],
  template: `
    <div
      class="doc-tree-panel"
      [ngClass]="styleClass"
      [ngStyle]="style"
      [class.doc-tree-loading]="loading">
      @if (filter) {
        <div class="doc-tree-filter mb-2">
          <input
            type="text"
            class="form-control form-control-sm"
            [(ngModel)]="filterText"
            [placeholder]="filterPlaceholder"
            (ngModelChange)="onFilterChange()" />
        </div>
      }

      @if (loading) {
        <div class="doc-tree-loading-indicator">
          <i class="pi pi-spinner pi-spin" aria-hidden="true"></i>
        </div>
      }

      <ul class="doc-tree-container list-unstyled mb-0">
        <ng-container
          *ngTemplateOutlet="nodeList; context: { $implicit: value ?? [], depth: 0 }">
        </ng-container>
      </ul>
    </div>

    <ng-template #nodeList let-nodes let-depth="depth">
      @for (node of nodes; track trackNode($index, node)) {
        @if (matchesFilter(node)) {
          <li class="doc-tree-node" [ngClass]="node.styleClass">
            <div
              class="doc-tree-node-content"
              [class.doc-tree-node-selected]="isSelected(node)"
              [class.p-highlight]="isSelected(node)"
              (click)="onNodeClick(node, $event)">
              @if (hasChildren(node)) {
                <button
                  type="button"
                  class="doc-tree-toggler btn btn-link p-0"
                  [attr.aria-expanded]="!!node.expanded"
                  (click)="toggleExpanded(node, $event)">
                  <i
                    class="pi"
                    [ngClass]="node.expanded ? 'pi-chevron-down' : 'pi-chevron-right'"
                    aria-hidden="true"></i>
                </button>
              } @else {
                <span class="doc-tree-toggler-spacer" aria-hidden="true"></span>
              }

              <div class="doc-tree-node-template flex-grow-1 min-w-0">
                @if (nodeTemplate) {
                  <ng-container
                    *ngTemplateOutlet="nodeTemplate.template; context: { $implicit: node, node: node }">
                  </ng-container>
                } @else {
                  @if (node.icon) {
                    <i [class]="node.icon + ' doc-tree-node-icon'" aria-hidden="true"></i>
                  }
                  <span class="doc-tree-node-label">{{ node.label }}</span>
                }
              </div>
            </div>

            @if (hasChildren(node) && node.expanded) {
              <ul class="doc-tree-children list-unstyled mb-0">
                <ng-container
                  *ngTemplateOutlet="nodeList; context: { $implicit: node.children, depth: depth + 1 }">
                </ng-container>
              </ul>
            }
          </li>
        }
      }
    </ng-template>
  `,
  styleUrl: './doc-tree.component.scss',
})
export class DocTreeComponent {
  @Input() value: DocTreeNode[] | null = [];
  @Input() selection: DocTreeNode | null = null;
  @Input() selectionMode: 'single' | 'multiple' | 'checkbox' | null = null;
  @Input() filter = false;
  @Input() filterBy = 'label';
  @Input() filterPlaceholder = 'Search...';
  @Input() loading = false;
  @Input() styleClass = '';
  @Input() style: Record<string, string> | null = null;
  @Input() emptyMessage = 'No results found';

  @Output() selectionChange = new EventEmitter<DocTreeNode | null>();
  @Output() onNodeSelect = new EventEmitter<DocTreeNodeSelectEvent>();
  @Output() onNodeExpand = new EventEmitter<DocTreeNodeSelectEvent>();
  @Output() onNodeCollapse = new EventEmitter<DocTreeNodeSelectEvent>();

  @ContentChild(DocTreeNodeTemplateDirective)
  nodeTemplate?: DocTreeNodeTemplateDirective;

  @HostBinding('class.doc-tree-host')
  readonly hostClass = true;

  filterText = '';

  trackNode(index: number, node: DocTreeNode): string | number {
    if (node.key != null) {
      return node.key;
    }

    const id = node.data?.['folderId'] ?? node.data?.['entityId'] ?? node.data?.['FolderId'];
    if (id != null) {
      return `${id}-${node.data?.['libraryType'] ?? ''}-${node.label ?? ''}`;
    }

    return `${index}-${node.label ?? ''}`;
  }

  hasChildren(node: DocTreeNode): boolean {
    return !node.leaf && (node.children?.length ?? 0) > 0;
  }

  isSelected(node: DocTreeNode): boolean {
    return docTreeNodesMatch(this.selection, node);
  }

  matchesFilter(node: DocTreeNode): boolean {
    if (!this.filter || !this.filterText.trim()) {
      return true;
    }

    const query = this.filterText.trim().toLowerCase();
    const fieldValue = String(getDocTreeNodeField(node, this.filterBy) ?? '').toLowerCase();
    if (fieldValue.includes(query)) {
      return true;
    }

    return (node.children ?? []).some((child) => this.matchesFilter(child));
  }

  onFilterChange(): void {
    if (!this.filterText.trim()) {
      return;
    }

    this.expandMatchingBranches(this.value ?? []);
  }

  onNodeClick(node: DocTreeNode, event: Event): void {
    if (node.selectable === false) {
      return;
    }

    if (this.selectionMode === 'single') {
      this.selection = node;
      this.selectionChange.emit(node);
    }

    this.onNodeSelect.emit({ node, originalEvent: event });
  }

  toggleExpanded(node: DocTreeNode, event: Event): void {
    event.stopPropagation();
    node.expanded = !node.expanded;

    const payload = { node, originalEvent: event };
    if (node.expanded) {
      this.onNodeExpand.emit(payload);
    } else {
      this.onNodeCollapse.emit(payload);
    }
  }

  private expandMatchingBranches(nodes: DocTreeNode[]): void {
    for (const node of nodes) {
      if (!node.children?.length) {
        continue;
      }

      this.expandMatchingBranches(node.children);
      if (node.children.some((child) => this.matchesFilter(child))) {
        node.expanded = true;
      }
    }
  }
}
